import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaChevronRight,
  FaCheck,
  FaBookOpen,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileLines,
  FaCopy,
} from "react-icons/fa6";

export const CoursePageShell: React.FC<{
  children: React.ReactNode;
  withStickyNav?: boolean;
}> = ({ children, withStickyNav }) => (
  <div
    className={`mx-auto w-full p-4 md:p-6 lg:p-8 max-w-5xl ${
      withStickyNav ? "pb-28 md:pb-8" : ""
    }`}
  >
    {children}
  </div>
);

export const CourseBreadcrumb: React.FC<{
  items: Array<{ label: string; to?: string }>;
  /** Microcopy ao lado do breadcrumb, ex: "33% concluído" */
  progressLabel?: string | null;
}> = ({ items, progressLabel }) => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm mb-5"
  >
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      {items.map((item, index) => (
        <React.Fragment key={`${item.label}-${index}`}>
          {index > 0 && (
            <FaChevronRight className="text-[10px] text-slate-400 shrink-0" />
          )}
          {item.to ? (
            <Link
              to={item.to}
              className="text-preppoBlue hover:text-preppoBlue/80 hover:underline underline-offset-2 truncate max-w-[10rem] sm:max-w-none font-semibold cursor-pointer transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-slate-800 font-semibold truncate max-w-[14rem] sm:max-w-none">
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
    {progressLabel ? (
      <>
        <span className="text-slate-300 hidden sm:inline" aria-hidden>
          ·
        </span>
        <span className="text-xs font-medium text-slate-500 tabular-nums">
          {progressLabel}
        </span>
      </>
    ) : null}
  </nav>
);

/** Barra de etapas: respondidas preenchidas, atuais em destaque, demais vazias. */
export const CourseStepper: React.FC<{
  total: number;
  currentIndex: number;
  /** Se informado, marca segmentos concluídos (independente do índice atual). */
  doneFlags?: boolean[];
  /** Contador textual (opcional — omitir quando os dots já bastam). */
  label?: string | null;
  nextHint?: string | null;
  /** Clique em etapa já respondida (índice). Etapas futuras não são clicáveis. */
  onStepClick?: (index: number) => void;
  /**
   * `bars` — segmentos horizontais (padrão).
   * `dots` — só numeração discreta (melhor para provas).
   */
  variant?: "bars" | "dots";
}> = ({
  total,
  currentIndex,
  doneFlags,
  label,
  nextHint,
  onStepClick,
  variant = "bars",
}) => {
  if (total <= 0) return null;
  const ariaLabel = label || `Questão ${currentIndex + 1} de ${total}`;

  if (variant === "dots") {
    return (
      <div role="navigation" aria-label={ariaLabel}>
        {(label || nextHint) && (
          <div className="flex items-center justify-between gap-3 mb-2.5">
            {label ? (
              <p className="text-xs font-medium text-slate-500 tabular-nums">
                {label}
              </p>
            ) : (
              <span />
            )}
            {nextHint ? (
              <p className="text-xs text-preppoBlue font-semibold shrink-0">
                {nextHint}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex items-center gap-1.5 max-w-sm">
          {Array.from({ length: total }, (_, i) => {
            const isCurrent = i === currentIndex;
            const isDone = doneFlags ? !!doneFlags[i] : i < currentIndex;
            const prevDone = doneFlags
              ? !!doneFlags[i - 1]
              : i - 1 < currentIndex;
            const canNavigate =
              !!onStepClick && (isDone || i === currentIndex || i < currentIndex);
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div
                    className={`h-px flex-1 min-w-[0.5rem] ${
                      prevDone ? "bg-preppoBlue/30" : "bg-slate-200"
                    }`}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  disabled={!canNavigate}
                  onClick={() => canNavigate && onStepClick?.(i)}
                  aria-label={`Questão ${i + 1}${isDone ? " (respondida)" : ""}${
                    isCurrent ? " (atual)" : ""
                  }`}
                  aria-current={isCurrent ? "step" : undefined}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-preppoBlue/30 ${
                    !canNavigate
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : isCurrent
                      ? "bg-preppoBlue text-white shadow-sm cursor-pointer"
                      : isDone
                      ? "bg-preppoBlue/10 text-preppoBlue hover:bg-preppoBlue/15 cursor-pointer"
                      : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300 cursor-pointer"
                  }`}
                >
                  {i + 1}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {(label || nextHint) && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {label ? (
            <p className="text-sm font-medium text-slate-600 tabular-nums">
              {label}
            </p>
          ) : (
            <span />
          )}
          {nextHint ? (
            <p className="text-xs text-preppoBlue font-semibold shrink-0">
              {nextHint}
            </p>
          ) : null}
        </div>
      )}
      <div className="flex items-center gap-1.5" role="list" aria-label={ariaLabel}>
        {Array.from({ length: total }, (_, i) => {
          const isCurrent = i === currentIndex;
          const isDone = doneFlags ? !!doneFlags[i] : i < currentIndex;
          const canNavigate =
            !!onStepClick && (isDone || i === currentIndex || i < currentIndex);
          return (
            <button
              key={i}
              type="button"
              role="listitem"
              disabled={!canNavigate}
              onClick={() => canNavigate && onStepClick?.(i)}
              aria-label={`Questão ${i + 1}${isDone ? " (respondida)" : ""}${
                isCurrent ? " (atual)" : ""
              }`}
              aria-current={isCurrent ? "step" : undefined}
              className={`relative h-1 flex-1 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-preppoBlue/40 ${
                canNavigate ? "cursor-pointer" : "cursor-default"
              } ${
                isCurrent
                  ? "bg-preppoBlue"
                  : isDone
                  ? "bg-preppoBlue/50 hover:bg-preppoBlue/70"
                  : "bg-slate-200"
              }`}
            >
              <span className="sr-only">{i + 1}</span>
            </button>
          );
        })}
      </div>
      {onStepClick && (
        <div className="mt-2 flex items-center gap-2">
          {Array.from({ length: total }, (_, i) => {
            const isCurrent = i === currentIndex;
            const isDone = doneFlags ? !!doneFlags[i] : i < currentIndex;
            const canNavigate = isDone || i <= currentIndex;
            return (
              <button
                key={i}
                type="button"
                disabled={!canNavigate}
                onClick={() => canNavigate && onStepClick(i)}
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-preppoBlue/40 ${
                  isCurrent
                    ? "bg-preppoBlue text-white"
                    : isDone
                    ? "bg-preppoBlue/15 text-preppoBlue hover:bg-preppoBlue/25 cursor-pointer"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
                aria-label={`Ir para questão ${i + 1}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Stepper horizontal de aulas (bolinhas). */
export const CourseLessonStepper: React.FC<{
  lessons: Array<{ id: number; unlocked: boolean; completed: boolean }>;
  currentLessonId: number;
  onSelect: (lessonId: number) => void;
}> = ({ lessons, currentLessonId, onSelect }) => {
  if (lessons.length <= 1) return null;
  return (
    <div
      className="mb-5 flex items-center gap-0 overflow-x-auto pb-1"
      role="navigation"
      aria-label="Aulas do curso"
    >
      {lessons.map((lesson, index) => {
        const isCurrent = lesson.id === currentLessonId;
        const canOpen = lesson.unlocked;
        return (
          <React.Fragment key={lesson.id}>
            {index > 0 && (
              <div
                className={`h-0.5 w-4 sm:w-6 shrink-0 ${
                  lessons[index - 1].completed
                    ? "bg-preppoBlue"
                    : "bg-slate-200"
                }`}
                aria-hidden
              />
            )}
            <button
              type="button"
              disabled={!canOpen}
              onClick={() => canOpen && onSelect(lesson.id)}
              title={
                canOpen
                  ? `Aula ${index + 1}`
                  : `Aula ${index + 1} (bloqueada)`
              }
              aria-current={isCurrent ? "step" : undefined}
              className={`relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-preppoBlue/40 ${
                !canOpen
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : isCurrent
                  ? "bg-preppoBlue text-white shadow-md scale-110 cursor-pointer"
                  : lesson.completed
                  ? "bg-preppoBlue text-white hover:bg-preppoBlue/90 cursor-pointer"
                  : "bg-white border-2 border-preppoBlue/40 text-preppoBlue hover:border-preppoBlue cursor-pointer"
              }`}
            >
              {lesson.completed && !isCurrent ? (
                <FaCheck className="text-[10px]" />
              ) : (
                index + 1
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/** Miniatura 16:9 do curso com badge "Concluído" no canto inferior esquerdo. */
export const CourseCoverThumb: React.FC<{
  coverUrl?: string | null;
  completed?: boolean;
}> = ({ coverUrl, completed }) => (
  <div
    aria-hidden
    className="relative w-28 sm:w-32 shrink-0 aspect-video rounded-lg overflow-hidden bg-slate-100 shadow-sm"
  >
    {coverUrl ? (
      <img
        src={coverUrl}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-preppoBlue/10 to-preppoBlue/25">
        <FaBookOpen className="text-preppoBlue/50 text-xl" />
      </div>
    )}
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent"
      aria-hidden
    />
    {completed ? (
      <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-md bg-emerald-600/95 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm ring-1 ring-white/30">
        <FaCheck className="text-[9px]" />
        Concluído
      </span>
    ) : null}
  </div>
);

/** Barra de progresso de seção (lista de cursos). */
export const CourseSectionProgress: React.FC<{
  completed: number;
  total: number;
  label: string;
}> = ({ completed, total, label }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const allDone = total > 0 && completed >= total;
  return (
    <div className="mt-2">
      <p
        className={`text-xs tabular-nums mb-1.5 ${
          allDone ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {label}
      </p>
      <div
        className="h-1.5 w-full max-w-[12rem] rounded-full bg-slate-200 overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            allDone ? "bg-emerald-500" : "bg-preppoBlue"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/** Status concluído como texto + ícone — sem pill. */
export const CourseStatusBadge: React.FC<{ label?: string }> = ({
  label = "Concluída",
}) => (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 shrink-0">
    <FaCheck className="text-[10px]" /> {label}
  </span>
);

type TooltipSide = "top" | "bottom";

const NavTooltip: React.FC<{
  text: string;
  show: boolean;
  side?: TooltipSide;
  children: React.ReactNode;
}> = ({ text, show, side = "top", children }) => {
  if (!show) return <>{children}</>;
  return (
    <span className="relative inline-flex group/tip">
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100 group-focus-within/tip:opacity-100 ${
          side === "top" ? "bottom-full mb-2" : "top-full mt-2"
        }`}
      >
        {text}
      </span>
    </span>
  );
};

export const CourseNavBar: React.FC<{
  onPrev?: () => void;
  prevDisabled?: boolean;
  prevLabel?: string;
  prevDisabledTooltip?: string;
  onNext?: () => void;
  nextDisabled?: boolean;
  nextLabel: string;
  nextPrimary?: boolean;
  nextDisabledTooltip?: string;
}> = ({
  onPrev,
  prevDisabled,
  prevLabel = "Anterior",
  prevDisabledTooltip,
  onNext,
  nextDisabled,
  nextLabel,
  nextPrimary = true,
  nextDisabledTooltip,
}) => {
  const prevIsDisabled = prevDisabled || !onPrev;
  const nextIsDisabled = nextDisabled || !onNext;

  return (
    <div className="fixed bottom-0 inset-x-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur-sm shadow-lg md:static md:mt-8 md:border-t md:border-slate-200 md:bg-transparent md:backdrop-blur-none md:pt-6 md:shadow-none">
      <div className="mx-auto w-full max-w-5xl px-4 py-3 md:px-0 flex items-center gap-3">
        <NavTooltip
          text={prevDisabledTooltip || ""}
          show={!!prevIsDisabled && !!prevDisabledTooltip}
          side="top"
        >
          <button
            type="button"
            disabled={prevIsDisabled}
            onClick={onPrev}
            title={
              prevIsDisabled && prevDisabledTooltip
                ? prevDisabledTooltip
                : undefined
            }
            className="inline-flex items-center justify-center gap-2 min-w-[7rem] px-4 py-2.5 rounded-lg border border-slate-300 text-slate-800 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 text-sm font-semibold transition-all"
          >
            {prevLabel}
          </button>
        </NavTooltip>
        <NavTooltip
          text={nextDisabledTooltip || ""}
          show={!!nextIsDisabled && !!nextDisabledTooltip}
          side="top"
        >
          <button
            type="button"
            disabled={nextIsDisabled}
            onClick={onNext}
            title={
              nextIsDisabled && nextDisabledTooltip
                ? nextDisabledTooltip
                : undefined
            }
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-preppoBlue/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm ${
              nextPrimary && !nextIsDisabled
                ? "bg-preppoBlue text-white hover:bg-preppoBlue/90 hover:shadow-md"
                : "border border-slate-200 text-slate-500 bg-slate-50"
            }`}
          >
            {nextLabel}
            <FaChevronRight className="text-xs" />
          </button>
        </NavTooltip>
      </div>
    </div>
  );
};

export const CourseCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div
    className={`bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm ${className}`}
  >
    {children}
  </div>
);

export const CourseLoading: React.FC<{ message?: string }> = ({
  message = "Carregando...",
}) => (
  <div className="mx-auto w-full p-4 md:p-6 lg:p-8 max-w-5xl">
    <div className="flex items-center justify-center min-h-[40vh]">
      <p className="text-slate-500 text-sm">{message}</p>
    </div>
  </div>
);

/** Skeleton da lista de cursos. */
export const CourseListSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4" aria-busy="true" aria-label="Carregando cursos">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-pulse"
      >
        <div className="px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-48 max-w-full rounded bg-slate-200" />
            <div className="h-1.5 w-28 rounded-full bg-slate-100" />
          </div>
          <div className="h-4 w-4 rounded bg-slate-200 shrink-0" />
        </div>
        {i === 0 && (
          <div className="border-t border-slate-100 p-4 space-y-3">
            {[0, 1].map((j) => (
              <div key={j} className="flex items-center gap-4">
                <div className="w-28 sm:w-32 aspect-video rounded-lg bg-slate-200 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 rounded bg-slate-200" />
                  <div className="h-3 w-full rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

export type MaterialFileKind = "pdf" | "docx" | "image" | "other";

export function getMaterialFileMeta(
  fileName?: string | null,
  mimeType?: string | null
): {
  kind: MaterialFileKind;
  label: string;
  iconClass: string;
  wrapClass: string;
} {
  const name = (fileName || "").toLowerCase();
  const mime = (mimeType || "").toLowerCase();

  if (mime.includes("pdf") || name.endsWith(".pdf")) {
    return {
      kind: "pdf",
      label: "PDF",
      iconClass: "text-red-600",
      wrapClass: "bg-red-50 text-red-600",
    };
  }
  if (
    mime.includes("word") ||
    mime.includes("officedocument.wordprocessing") ||
    name.endsWith(".docx") ||
    name.endsWith(".doc")
  ) {
    return {
      kind: "docx",
      label: "DOCX",
      iconClass: "text-blue-600",
      wrapClass: "bg-blue-50 text-blue-600",
    };
  }
  if (
    mime.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)
  ) {
    return {
      kind: "image",
      label: "Imagem",
      iconClass: "text-emerald-600",
      wrapClass: "bg-emerald-50 text-emerald-600",
    };
  }
  return {
    kind: "other",
    label: "Arquivo",
    iconClass: "text-slate-500",
    wrapClass: "bg-slate-100 text-slate-500",
  };
}

export function formatFileSize(bytes?: number | null): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const MaterialTypeIcon: React.FC<{
  fileName?: string | null;
  mimeType?: string | null;
  size?: "sm" | "md";
  className?: string;
}> = ({ fileName, mimeType, size = "md", className = "" }) => {
  const meta = getMaterialFileMeta(fileName, mimeType);
  const Icon =
    meta.kind === "pdf"
      ? FaFilePdf
      : meta.kind === "docx"
      ? FaFileWord
      : meta.kind === "image"
      ? FaFileImage
      : FaFileLines;
  const box = size === "sm" ? "h-8 w-8" : "h-9 w-9";
  return (
    <span
      className={`inline-flex ${box} shrink-0 items-center justify-center rounded-lg ${meta.wrapClass} ${className}`}
      aria-hidden
    >
      <Icon className={`text-base ${meta.iconClass}`} />
    </span>
  );
};

/** Código de validação + ações secundárias no mesmo estilo de texto. */
export const CertificateCodeChip: React.FC<{
  code: string;
  validateHref?: string;
}> = ({ code, validateHref }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // fallback silencioso
    }
  };

  const actionClass =
    "inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors";

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
      <span className="font-mono tracking-wider text-slate-700 tabular-nums">
        {code}
      </span>
      <button type="button" onClick={handleCopy} className={actionClass}>
        {copied ? (
          <>
            <FaCheck className="text-emerald-600 text-[10px]" />
            Copiado
          </>
        ) : (
          <>
            <FaCopy className="text-[10px]" />
            Copiar
          </>
        )}
      </button>
      {validateHref ? (
        <a
          href={validateHref}
          target="_blank"
          rel="noreferrer"
          className={actionClass}
        >
          Validar
        </a>
      ) : null}
    </div>
  );
};

/** Confirmação simples (modal) sem dependência extra de estado global. */
export const CourseConfirmModal: React.FC<{
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}> = ({
  open,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Revisar",
  onConfirm,
  onCancel,
  busy,
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="course-confirm-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        aria-label="Fechar"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
        <h2
          id="course-confirm-title"
          className="text-lg font-bold text-slate-900 tracking-tight"
        >
          {title}
        </h2>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {description}
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-800 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 transition-all"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-preppoBlue text-white text-sm font-semibold hover:bg-preppoBlue/90 disabled:opacity-50 shadow-sm transition-all"
          >
            {busy ? "Enviando..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Microanimação de celebração (CSS, sem lib). */
export const CourseCelebrateBurst: React.FC<{ active: boolean }> = ({
  active,
}) => {
  if (!active) return null;
  const pieces = Array.from({ length: 14 }, (_, i) => i);
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl"
      aria-hidden
    >
      {pieces.map((i) => (
        <span
          key={i}
          className="absolute top-1/2 left-1/2 h-2 w-2 rounded-sm opacity-0 animate-[course-confetti_900ms_ease-out_forwards]"
          style={{
            backgroundColor:
              i % 3 === 0 ? "#1d4ed8" : i % 3 === 1 ? "#10b981" : "#f59e0b",
            transform: `rotate(${i * 26}deg)`,
            animationDelay: `${i * 28}ms`,
            ["--dx" as string]: `${Math.cos((i / 14) * Math.PI * 2) * 70}px`,
            ["--dy" as string]: `${Math.sin((i / 14) * Math.PI * 2) * 42 - 20}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes course-confetti {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% {
            opacity: 0;
            transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.4);
          }
        }
      `}</style>
    </div>
  );
};
